import React, { useCallback, useMemo, memo } from 'react';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import { truncateIfTooLong } from 'src/util/truncate';
import { useWalletAuth } from 'src/hooks/useWalletAuth';
import { useAuth } from 'src/contexts/AuthContext';
import { GradientIconButton } from '../gradients/GradientIconButton';
import { GradientTypography } from '../gradients/GradientTypography';
import { useTheme } from '@mui/material/styles';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import Typography from '@mui/material/Typography';
import { MenuItemWallet } from './MenuItemWallet';
import { useSelectedAddress } from 'src/hooks/useSelectedAddress';

export const SelectWalletAddress = memo(function SelectWalletAddress() {
  const { resetWallet } = useWalletAuth();
  const { userData } = useAuth();
  const { selectedAddress, isConnectedAddress } = useSelectedAddress();
  const theme = useTheme();
  const userAddresses = userData?.addresses;
  const renderValue = (value: string) => {
    return (
      <Typography variant="buttonLarge">{truncateIfTooLong(value)}</Typography>
    );
  };
  const onResetClick = useCallback(async () => {
    await resetWallet();
  }, [resetWallet]);

  // eslint-disable-next-line react-memo/require-memo
  const WalletSelectStartAdornment = useMemo(() => {
    if (isConnectedAddress) {
      return undefined;
    }
    return (
      <AccountBalanceWalletOutlinedIcon
        sx={{
          mr: 2,
          ml: '20px',
          color: theme.palette.primary.main,
          height: '22px',
          width: '22px',
        }}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnectedAddress]);

  // eslint-disable-next-line react-memo/require-memo
  const MenuItemWallets = useMemo(() => {
    return userAddresses?.map((address, _index) => {
      return (
        <MenuItemWallet
          address={address}
          key={`${address}`}
          onClick={onResetClick}
          isSelected={
            !!isConnectedAddress && selectedAddress === String(address)
          }
        />
      );
    });
  }, [userAddresses, onResetClick, isConnectedAddress, selectedAddress]);

  return (
    <Select
      value={selectedAddress}
      displayEmpty
      startAdornment={WalletSelectStartAdornment}
      inputProps={{ 'aria-label': 'Wallet Select' }}
      sx={{
        height: '40px',
        width: '175px',
        '&.MuiInputBase-adornedStart': {
          pl: 0,
        },
      }}
      renderValue={renderValue}
    >
      {MenuItemWallets}
      <MenuItem onClick={onResetClick}>
        <GradientIconButton
          IconComponent={AddIcon}
          disableMobile
          size="small"
          sxOuter={{ p: 0, ml: -1 }}
        />
        <GradientTypography
          gradientColor="primary.light"
          variant="body1"
          sx={{ fontWeight: 500 }}
        >
          WALLET ADDRESS
        </GradientTypography>
      </MenuItem>
    </Select>
  );
});
